import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Analytics, initializeAnalytics, isSupported, logEvent, setUserId } from 'firebase/analytics';
import { filter, Subscription } from 'rxjs';
import { AuthService } from './auth.service';
import { FirebaseService } from './firebase.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private analyticsPromise?: Promise<Analytics | null>;
  private subscriptions = new Subscription();
  private started = false;
  private lastPagePath: string | null = null;
  private currentPagePath: string | null = null;
  private currentUserId: string | null = null;
  private analytics: Analytics | null = null;
  private readonly onlineHandler = () => this.startAnalyticsWhenOnline();

  constructor(
    private router: Router,
    private authService: AuthService,
    private firebaseService: FirebaseService
  ) { }

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.currentPagePath = this.router.url;

    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => {
          this.currentPagePath = event.urlAfterRedirects;
          this.logPageView(event.urlAfterRedirects);
        })
    );

    this.subscriptions.add(
      this.authService.user$.subscribe(user => {
        this.currentUserId = user?.uid ?? null;
        this.setCurrentUser(this.currentUserId);
      })
    );

    this.startAnalyticsWhenOnline();
  }

  private startAnalyticsWhenOnline(): void {
    if (this.analyticsPromise || typeof window === 'undefined') {
      return;
    }

    // Firebase Installations cannot register while the browser is offline and
    // logs a console error before Analytics has a chance to recover.
    if (!navigator.onLine) {
      window.addEventListener('online', this.onlineHandler, { once: true });
      return;
    }

    window.removeEventListener('online', this.onlineHandler);
    this.analyticsPromise = this.initializeFirebaseAnalytics();
    this.analyticsPromise.then(analytics => {
      this.analytics = analytics;

      if (!analytics) {
        return;
      }

      this.logPageView(this.currentPagePath || this.router.url);
      this.setCurrentUser(this.currentUserId);
    });
  }

  private async initializeFirebaseAnalytics(): Promise<Analytics | null> {
    try {
      if (typeof window === 'undefined' || !(await isSupported())) {
        return null;
      }

      return initializeAnalytics(this.firebaseService.app, {
        config: {
          send_page_view: false
        }
      });
    } catch (error) {
      console.warn('Firebase Analytics initialization failed:', error);
      return null;
    }
  }

  private async logPageView(pagePath: string): Promise<void> {
    if (!this.analytics || pagePath === this.lastPagePath || typeof window === 'undefined') {
      return;
    }

    this.lastPagePath = pagePath;

    logEvent(this.analytics, 'page_view', {
      page_title: document.title,
      page_location: `${window.location.origin}${pagePath}`,
      page_path: pagePath
    });
  }

  private async setCurrentUser(userId: string | null): Promise<void> {
    if (!this.analytics) {
      return;
    }

    setUserId(this.analytics, userId);
  }
}
